import { BroadcastChannel, createLeaderElection } from 'broadcast-channel';

type ApiMessage<T> = { type: 'API_RESPONSE'; payload: T };

type CacheEnvelope<T> = { data: T; timestamp: number };

function readCache<T>(cacheKey: string, ttlMs: number): T | null {
  try {
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEnvelope<T>;
    if (Date.now() - parsed.timestamp > ttlMs) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache<T>(cacheKey: string, data: T): void {
  try {
    const envelope: CacheEnvelope<T> = { data, timestamp: Date.now() };
    localStorage.setItem(cacheKey, JSON.stringify(envelope));
  } catch {}
}

// Simple cache + broadcast helper with leader election (only leader fetches)
export async function fetchWithBroadcast<T>(
  channelName: string,
  cacheKey: string,
  fetcher: () => Promise<T>,
  options?: { ttlMs?: number; waitTimeoutMs?: number }
): Promise<T> {
  const ttlMs = options?.ttlMs ?? 60_000;
  const waitTimeoutMs = options?.waitTimeoutMs ?? 3000; // used only to bound waiting for an immediate broadcast before leadership resolves

  // 1. Check cache first
  const cached = readCache<T>(cacheKey, ttlMs);
  if (cached) return cached;

  const bc = new BroadcastChannel<ApiMessage<T>>(channelName);
  const elector = createLeaderElection(bc);

  // 2. Listen for any peer broadcast immediately (fast path)
  const waitForBroadcast = new Promise<T>((resolve) => {
    const timer = setTimeout(() => {
      bc.onmessage = null as any;
    }, waitTimeoutMs);
    bc.onmessage = (msg) => {
      if (msg?.type === 'API_RESPONSE') {
        clearTimeout(timer);
        writeCache(cacheKey, msg.payload);
        resolve(msg.payload);
      }
    };
  });

  // 3. Compete for leadership; the elected leader performs the fetch and broadcasts
  const leaderFetch = (async () => {
    await elector.awaitLeadership();
    const data = await fetcher();
    writeCache(cacheKey, data);
    await bc.postMessage({ type: 'API_RESPONSE', payload: data });
    return data;
  })();

  try {
    // Prefer fast peer broadcast if it arrives in the initial window; otherwise the leader fetch resolves.
    return await Promise.race([waitForBroadcast, leaderFetch]);
  } finally {
    elector.die();
    bc.close();
  }
}
